import { type FormOptionV2, GetFormsProductTypeEnum } from "@credit-acceptance/step6-bff-client";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { workbenchAncillaryProductOptionsApi } from "../../../../../api/client";
import { ancillaryFormsKeys } from "./ancillaryFormsKeys";

export type AncillaryProductType = (typeof GetFormsProductTypeEnum)[keyof typeof GetFormsProductTypeEnum];

/**
 * Shared queryOptions for a vendor's TPAP form-number list (key + queryFn), backed by the generated
 * `WorkbenchAncillaryProductOptionsApi.getForms`. The BFF never caches this list, so neither do we. A response
 * whose echoed product/vendor doesn't match the request is dropped, never shown against the wrong vendor.
 * @param approvalId The deal's approval id.
 * @param productType `VSC` or `GAP`.
 * @param vendorId The selected vendor's id, from `additionalProducts.options.<product>.vendors`.
 * @returns {ReturnType<typeof queryOptions>} Typed options object.
 */
export function ancillaryFormsQuery(
  approvalId: string | undefined,
  productType: AncillaryProductType,
  vendorId: number | undefined
) {
  return queryOptions({
    queryKey: ancillaryFormsKeys.list(approvalId, productType, vendorId),
    queryFn: async ({ signal }): Promise<FormOptionV2[]> => {
      const { data } = await workbenchAncillaryProductOptionsApi.getForms(
        approvalId as string,
        productType,
        String(vendorId),
        { signal }
      );
      const isStale = (data.productType && data.productType !== productType) || data.vendorId !== vendorId;
      return isStale ? [] : (data.forms ?? []);
    },
    enabled: !!approvalId && vendorId !== undefined,
    staleTime: 0,
  });
}

/**
 * Reads the form numbers offered for a TPAP product's selected vendor. Refetches whenever the vendor changes.
 * @param productType `VSC` or `GAP`.
 * @param vendorId The selected vendor's id; the request stays idle until one exists.
 * @param enabled Extra gate — callers pass `false` for cards that aren't editable TPAP.
 * @returns {ReturnType<typeof useQuery<FormOptionV2[]>>} The TanStack Query result.
 */
export function useAncillaryForms(productType: AncillaryProductType, vendorId: number | undefined, enabled: boolean) {
  const { approvalId } = useParams<{ approvalId: string }>();
  const options = ancillaryFormsQuery(approvalId, productType, vendorId);
  return useQuery({ ...options, enabled: enabled && options.enabled });
}
